import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, Routes, Route, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowPathIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FilmIcon,
  FunnelIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import DashboardLayout from "../../components/DashboardLayout";
import AudienceInfo from "../../components/audiences/AudienceInfo";
import AudienceFiltersPopover from "../../components/audiences/AudienceFiltersPopover";
import AudienceHeaderIcon from "../../components/audiences/AudienceHeaderIcon";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import {
  useGetAudiencesForProjectQuery,
  useDeleteAudienceMutation,
} from "../../redux/api/audiencesApi";
import CreateAudience from "./CreateAudience";
import PopulatingModal from "./PopulatingModal";
import { getPlatformIcon, getPlatformColor } from "../../utils/platforms";
import { getTotalPages } from "../../utils/pagination";
import { formatCount } from "../../utils/numbers";
import {
  ALL_AUDIENCE_TYPES,
  AUDIENCE_PAGE_SIZE,
  getAudienceDisplayName,
  resolveAudienceListBrandHash,
} from "../../constants/audiences";

const Audiences = ({ currentUser }) => {
  const location = useLocation();
  const { currentProject, projects, loaded: projectsLoaded } = useSelector((state) => state.project);
  const { currentBrand, brands, ready: brandsReady } = useSelector((state) => state.brand);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showFiltersPopover, setShowFiltersPopover] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("fit_score");
  const [selectedTypes, setSelectedTypes] = useState(ALL_AUDIENCE_TYPES);
  const [platformFilter, setPlatformFilter] = useState("");
  const [showLowFitAudiences, setShowLowFitAudiences] = useState(false);
  const [page, setPage] = useState(1);
  const filtersContainerRef = useRef(null);
  const prevPathnameRef = useRef(location.pathname);

  const typesFilterActive = selectedTypes.length < ALL_AUDIENCE_TYPES.length;
  const activeFilterCount = sortBy !== "fit_score" ? 1 : 0;

  const queryArg = useMemo(() => {
    if (!currentProject?.hash || !projectsLoaded || !brandsReady) return null;
    const params = {
      project_hash: currentProject.hash,
      page,
      page_size: AUDIENCE_PAGE_SIZE,
      sort_by: sortBy,
    };
    const brandHash = resolveAudienceListBrandHash(currentProject, currentBrand, brands);
    if (brandHash) params.brand_hash = brandHash;
    if (search) params.search = search;
    if (typesFilterActive) params.audience_type = selectedTypes.join(",");
    if (platformFilter) params.platform = platformFilter;
    return params;
  }, [
    brands,
    brandsReady,
    currentBrand,
    currentProject,
    page,
    platformFilter,
    projectsLoaded,
    search,
    selectedTypes,
    sortBy,
    typesFilterActive,
  ]);

  const { data, isLoading, isFetching, isError, refetch } = useGetAudiencesForProjectQuery(
    queryArg,
    { skip: !queryArg }
  );
  const [deleteAudience] = useDeleteAudienceMutation();

  const audiences = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const nextPageUrl = data?.next ?? null;
  const prevPageUrl = data?.previous ?? null;
  const totalPages = getTotalPages(totalCount, AUDIENCE_PAGE_SIZE);
  const showPagination = audiences.length > 0 && totalPages > 1;

  useEffect(() => {
    if (!queryArg) return;
    const pathname = location.pathname;
    const onList = pathname === "/research/" || pathname === "/research";
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (!onList) return;

    // Stay within /research/* (e.g. close /research/new/ modal) → keep cache
    const wasOnResearch = prev === "/research" || prev === "/research/" || prev.startsWith("/research/");
    if (wasOnResearch && prev !== pathname) return;

    refetch();
  }, [location.pathname, queryArg, refetch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [currentProject?.hash, currentBrand?.hash, sortBy, selectedTypes, platformFilter]);

  useEffect(() => {
    setShowLowFitAudiences(false);
  }, [currentProject?.hash, currentBrand?.hash]);

  const resetPopoverFilters = () => {
    setSortBy("fit_score");
    setSelectedTypes(ALL_AUDIENCE_TYPES);
    setPlatformFilter("");
    setPage(1);
  };

  const clearAllFilters = () => {
    setSearchInput("");
    setSearch("");
    resetPopoverFilters();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest(`[data-menu-id="${openMenuId}"]`)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const handleDeleteAudience = async (audienceHash, audienceName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${audienceName || "this audience"}"? This action cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteAudience({
        hash: audienceHash,
        projectHash: currentProject.hash,
      }).unwrap();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to delete audience:", err);
      alert("Failed to delete audience. Please try again.");
    }
  };

  const hasFilters = search.trim() || activeFilterCount > 0;
  const showFilteredEmptyState =
    !isLoading && !isFetching && !isError && totalCount === 0 && Boolean(hasFilters);
  const showListToolbar = totalCount > 0 || hasFilters;

  const storedProjectHash =
    typeof localStorage !== "undefined" ? localStorage.getItem("currentProjectHash") : null;
  const listLoading =
    (!currentProject?.hash && (Boolean(storedProjectHash) || projects.length > 0)) ||
    (Boolean(currentProject?.hash) && (!projectsLoaded || !brandsReady || (isLoading && !audiences.length)));
  const showEmptyInfo =
    Boolean(currentProject?.hash) &&
    !listLoading &&
    totalCount === 0 &&
    !hasFilters;

  const highFitAudiences = useMemo(
    () => audiences.filter((audience) => audience.high_fit !== false),
    [audiences]
  );
  const lowFitAudiences = useMemo(
    () => audiences.filter((audience) => audience.high_fit === false),
    [audiences]
  );

  const renderRevealCard = (icon, label, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className="relative rounded-lg p-6 h-full w-full flex flex-col items-center justify-center gap-2 text-center border-2 border-dashed border-white/25 hover:border-white/50 transition-colors"
    >
      <span className="text-theme-text opacity-50" aria-hidden>
        {icon}
      </span>
      <span className="text-sm text-theme-text opacity-70">{label}</span>
    </button>
  );

  const renderAudienceGrid = () => {
    const lowFitCount = lowFitAudiences.length;
    const lowFitLabel =
      lowFitCount === 1
        ? "Show 1 low-fit audience"
        : `Show ${lowFitCount} low-fit audiences`;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
        {highFitAudiences.map((audience) => renderAudienceCard(audience))}
        {!showLowFitAudiences && lowFitCount > 0
          ? renderRevealCard(
              <ExclamationTriangleIcon className="h-6 w-6" />,
              lowFitLabel,
              () => setShowLowFitAudiences(true)
            )
          : null}
        {showLowFitAudiences
          ? lowFitAudiences.map((audience) => renderAudienceCard(audience, { lowFit: true }))
          : null}
      </div>
    );
  };

  const renderAudienceCard = (audience, { lowFit = false } = {}) => {
    const platform = audience.category_platform || audience.platform;
    const platformIcon = getPlatformIcon(platform);
    const displayName = getAudienceDisplayName(audience);
    const showPlatformInStats = Boolean(platform && platformIcon);

    return (
      <div
        key={audience.hash}
        className="relative bg-theme-secondary rounded-lg p-6 hover:shadow-lg transition-shadow border border-theme-secondary"
        data-menu-id={audience.hash}
      >
        <div className="flex items-start justify-between mb-3">
          <Link
            to={`/audience/${audience.hash}/`}
            className="flex items-center gap-2 flex-1 min-w-0 group"
          >
            <div
              className="flex-shrink-0 w-4 h-4 flex items-center justify-center leading-none"
              aria-hidden
            >
              <AudienceHeaderIcon audience={audience} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-theme-text truncate group-hover:underline">{displayName}</h3>
            </div>
          </Link>
          <div className="relative">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpenMenuId(openMenuId === audience.hash ? null : audience.hash);
              }}
              className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors text-theme-text opacity-70 hover:opacity-100"
              aria-label="More options"
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"
                />
              </svg>
            </button>
            {openMenuId === audience.hash && (
              <div className="absolute right-0 top-8 w-48 bg-theme-main border border-theme-secondary rounded-lg shadow-lg z-50">
                <div className="py-1">
                  <Link
                    to={`/audience/${audience.hash}/`}
                    onClick={() => setOpenMenuId(null)}
                    className="block w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
                  >
                    View
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteAudience(audience.hash, displayName);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-theme-text opacity-60">
          <span className="flex items-center gap-3">
            {showPlatformInStats && platformIcon ? (
              <span
                className="shrink-0 flex items-center justify-center w-4 h-4"
                style={{
                  color: getPlatformColor(platform) || "currentColor",
                }}
                title={platform}
                aria-hidden
              >
                {React.cloneElement(platformIcon, { className: "w-4 h-4" })}
              </span>
            ) : null}
            <span className="flex items-center gap-1" title="Creators">
              <UserGroupIcon className="h-4 w-4 shrink-0" aria-hidden />
              {formatCount(audience.creator_count)}
            </span>
            <span className="flex items-center gap-1" title="Videos">
              <FilmIcon className="h-4 w-4 shrink-0" aria-hidden />
              {formatCount(audience.high_fit_video_count)}
            </span>
            {lowFit ? (
              <span className="flex items-center gap-1 opacity-80" title="Low brand fit">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden />
                Low Fit
              </span>
            ) : null}
          </span>
          {audience.audience_type === "category" &&
            (audience.videos_synced_at ? (
              <span className="flex items-center gap-1 shrink-0">
                <ArrowPathIcon className="h-4 w-4 shrink-0" aria-hidden />
                {new Date(audience.videos_synced_at).toLocaleDateString()}
              </span>
            ) : (
              <span className="flex items-center gap-1 shrink-0">
                <ClockIcon className="h-4 w-4 shrink-0" aria-hidden />
                Populating...
              </span>
            ))}
        </div>
      </div>
    );
  };

  const primaryAction = (
    <Link
      to="/research/new/"
      className="inline-flex items-center gap-2 px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
    >
      <PlusIcon className="h-4 w-4 shrink-0" aria-hidden />
      New Audience
    </Link>
  );

  return (
    <DashboardLayout
      title="Audience Research"
      currentUser={currentUser}
      primaryAction={primaryAction}
      secondaryActions={[]}
    >
      <div className="space-y-8">
        {listLoading ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <OrbitalLoader size={128} />
          </div>
        ) : isError ? (
          <div className="text-center py-10 text-theme-text border border-theme-secondary rounded-lg bg-theme-secondary space-y-3">
            <p className="opacity-80">Could not load audiences.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {showListToolbar ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-xl font-semibold text-theme-text">
                    Your Audiences{" "}
                    <span className="text-sm font-normal opacity-50">({totalCount})</span>
                  </h2>
                  {hasFilters ? (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="text-xs text-theme-text opacity-75 hover:underline"
                    >
                      Clear filters
                    </button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search audiences"
                    className="h-8 w-48 px-2.5 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs"
                  />
                  <div className="relative" ref={filtersContainerRef}>
                    <button
                      type="button"
                      onClick={() => setShowFiltersPopover((value) => !value)}
                      className={`flex items-center justify-center h-8 w-8 rounded border transition-colors ${
                        activeFilterCount > 0
                          ? "bg-theme-primary/10 border-theme-primary text-theme-primary"
                          : "bg-theme-secondary border-theme-secondary text-theme-text hover:border-theme-text hover:border-opacity-30"
                      }`}
                      aria-label="Filters"
                    >
                      <FunnelIcon className="h-4 w-4" />
                      {activeFilterCount > 0 ? (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[1.125rem] h-[1.125rem] px-1 text-[10px] font-semibold rounded-full bg-theme-primary text-theme-button flex items-center justify-center">
                          {activeFilterCount}
                        </span>
                      ) : null}
                    </button>
                    <AudienceFiltersPopover
                      isOpen={showFiltersPopover}
                      onClose={() => setShowFiltersPopover(false)}
                      containerRef={filtersContainerRef}
                      sortBy={sortBy}
                      setSortBy={setSortBy}
                      selectedTypes={selectedTypes}
                      setSelectedTypes={setSelectedTypes}
                      platformFilter={platformFilter}
                      setPlatformFilter={setPlatformFilter}
                      onClearAll={resetPopoverFilters}
                      activeFilterCount={activeFilterCount}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {showFilteredEmptyState ? (
              <>
                <div className="text-center py-10 text-theme-text opacity-80 border border-theme-secondary rounded-lg bg-theme-secondary">
                  <p className="mb-4">No audiences match your filters.</p>
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
                  >
                    Reset filters
                  </button>
                </div>
                {renderAudienceGrid()}
              </>
            ) : showEmptyInfo ? (
              <AudienceInfo />
            ) : (
              <div className="space-y-4">
                {renderAudienceGrid()}

                {showPagination ? (
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-xs text-theme-text opacity-70">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPage((value) => Math.max(1, value - 1))}
                        disabled={!prevPageUrl || isFetching}
                        className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage((value) => value + 1)}
                        disabled={!nextPageUrl || isFetching}
                        className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
      <Routes>
        <Route path="new/*" element={<CreateAudience currentUser={currentUser} />}>
          <Route path="populating/:hash" element={<PopulatingModal />} />
        </Route>
      </Routes>
    </DashboardLayout>
  );
};

export default Audiences;
